
import React from 'react';
import { ScoreBreakdown as ScoreBreakdownType } from '@/utils/scoreCalculator';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface ScoreBreakdownProps {
  breakdown: ScoreBreakdownType;
  className?: string;
}

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ breakdown, className }) => {
  const getProgressColor = (score: number, max: number) => {
    const percent = (score / max) * 100;
    if (percent >= 85) return 'bg-green-500';
    if (percent >= 70) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const categories = [
    { 
      name: 'Finger Effort & Key Position', 
      value: breakdown.fingerEffort,
      max: 20,
      description: 'Measures how comfortable your fingers are when typing this domain' 
    },
    { 
      name: 'Hand Alternation & Flow', 
      value: breakdown.handAlternation,
      max: 20,
      description: 'Evaluates how well typing alternates between hands' 
    },
    { 
      name: 'Repetition & Pattern Complexity', 
      value: breakdown.repetitionComplexity,
      max: 20,
      description: 'Checks for difficult patterns that could cause typos' 
    },
    { 
      name: 'Length & Simplicity', 
      value: breakdown.lengthSimplicity,
      max: 20,
      description: 'Considers domain length and character complexity' 
    },
    { 
      name: 'Phonetic Clarity', 
      value: breakdown.phoneticClarity,
      max: 20,
      description: 'Measures how easy the domain is to pronounce and remember' 
    }
  ];

  return (
    <div className={cn("space-y-4", className)}>
      <h3 className="text-lg font-medium mb-3">Score Breakdown</h3>
      
      <div className="space-y-4">
        {categories.map((category) => (
          <div key={category.name} className="space-y-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium">{category.name}</span>
              <span className="text-sm font-medium">{category.value}/{category.max}</span>
            </div>
            <Progress 
              value={(category.value / category.max) * 100} 
              className={cn("h-2", getProgressColor(category.value, category.max))}
            />
            <p className="text-xs text-gray-500">{category.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScoreBreakdown;
